"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.siPrefixes = void 0;
exports.correctFloatingPointErrors = correctFloatingPointErrors;
exports.formatNumericalSIValue = formatNumericalSIValue;
exports.parseNumericalSIValue = parseNumericalSIValue;
const RkmCodeValueTool_1 = require("./RkmCodeValueTool");
exports.siPrefixes = [
    {
        name: 'E',
        factor: 1e18,
        match: (unitsStr) => unitsStr === 'E' || unitsStr.toLowerCase() === 'exa',
        transform: (value) => parseNumber(value) * 1e18,
    },
    {
        name: 'P',
        factor: 1e15,
        match: (unitsStr) => unitsStr === 'P' || unitsStr.toLowerCase() === 'peta',
        transform: (value) => parseNumber(value) * 1e15,
    },
    {
        name: 'T',
        factor: 1e12,
        match: (unitsStr) => unitsStr.toUpperCase() === 'T' || unitsStr.toLowerCase() === 'tera',
        transform: (value) => parseNumber(value) * 1e12,
    },
    {
        name: 'G',
        factor: 1e9,
        match: (unitsStr) => unitsStr.toUpperCase() === 'G' || unitsStr.toLowerCase() === 'giga',
        transform: (value) => parseNumber(value) * 1e9,
    },
    {
        name: 'M',
        factor: 1e6,
        match: (unitsStr) => unitsStr === 'M' || unitsStr.toLowerCase() === 'mega',
        transform: (value) => parseNumber(value) * 1e6,
    },
    {
        name: 'k',
        factor: 1e3,
        match: (unitsStr) => ['k', 'kilo'].includes(unitsStr.toLowerCase()),
        transform: (value) => parseNumber(value) * 1e3,
    },
    {
        name: '',
        factor: 1,
        match: (unitsStr) => unitsStr === '',
        transform: (value) => parseNumber(value),
    },
    {
        name: 'd',
        factor: 1e-1,
        match: (unitsStr) => unitsStr.toLowerCase() === 'd' || unitsStr.toLowerCase() === 'deci',
        transform: (value) => parseNumber(value) * 1e-1,
    },
    {
        name: 'c',
        factor: 1e-2,
        match: (unitsStr) => unitsStr.toLowerCase() === 'c' || unitsStr.toLowerCase() === 'centi',
        transform: (value) => parseNumber(value) * 1e-2,
    },
    {
        name: 'm',
        factor: 1e-3,
        match: (unitsStr) => unitsStr === 'm' || unitsStr.toLowerCase() === 'milli',
        transform: (value) => parseNumber(value) * 1e-3,
    },
    {
        name: 'µ',
        factor: 1e-6,
        match: (unitsStr) => unitsStr.toLowerCase() === 'u' || unitsStr === 'µ' || unitsStr === 'μ' || unitsStr.toLowerCase() === 'micro',
        transform: (value) => parseNumber(value) * 1e-6,
    },
    {
        name: 'n',
        factor: 1e-9,
        match: (unitsStr) => unitsStr.toLowerCase() === 'n' || unitsStr.toLowerCase() === 'nano',
        transform: (value) => parseNumber(value) * 1e-9,
    },
    {
        name: 'p',
        factor: 1e-12,
        match: (unitsStr) => unitsStr === 'p' || unitsStr.toLowerCase() === 'pico',
        transform: (value) => parseNumber(value) * 1e-12,
    },
    {
        name: 'f',
        factor: 1e-15,
        match: (unitsStr) => unitsStr.toLowerCase() === 'f' || unitsStr.toLowerCase() === 'femto',
        transform: (value) => parseNumber(value) * 1e-15,
    },
];
function correctFloatingPointErrors(value) {
    if (!Number.isFinite(value)) {
        return value;
    }
    return parseFloat(value.toPrecision(15));
}
function formatNumericalSIValue(value, precision = 3) {
    if (value === 0) {
        return '0';
    }
    let sig = Math.abs(value);
    let exponent = 0;
    while (sig >= 1000 && exponent < 24) {
        sig /= 1000;
        exponent += 3;
    }
    while (sig < 1 && exponent > -24) {
        sig *= 1000;
        exponent -= 3;
    }
    const prefix = exports.siPrefixes.find((p) => p.factor === Math.pow(10, exponent));
    const prefixName = prefix ? prefix.name : '';
    const signPrefix = value < 0 ? '-' : '';
    if (sig > 1000) {
        return signPrefix + sig.toFixed(0) + prefixName;
    }
    return signPrefix + parseFloat(sig.toPrecision(precision)) + prefixName;
}
function parseNumericalSIValue(value, { short, long, alternateNames: alternates, usesSIPrefixes }) {
    if (typeof value === 'undefined' || value === null) {
        return undefined;
    }
    if (typeof value === 'number') {
        return value;
    }
    value = value.trim();
    if (value.startsWith('.')) {
        value = '0' + value;
    }
    let firstException;
    try {
        return maybeParseSiValue(value, { short, long, alternateNames: alternates, usesSIPrefixes });
    }
    catch (ex) {
        firstException = ex && ex instanceof Error ? ex : new Error(`Error parsing SI value: ${ex}`);
    }
    const valueStr = (0, RkmCodeValueTool_1.convertRkmValueToSiValueStr)(value);
    if (!valueStr) {
        throw firstException;
    }
    try {
        return maybeParseSiValue(valueStr, { short, long, alternateNames: alternates, usesSIPrefixes });
    }
    catch {
        throw firstException;
    }
}
function maybeParseSiValue(value, { short, long, alternateNames: alternates, usesSIPrefixes }) {
    if (typeof value === 'undefined' || value === null) {
        return undefined;
    }
    if (typeof value === 'number') {
        return value;
    }
    value = value.trim();
    if (/^-?\d*(\.\d*)?(e[-+]?[0-9]+)?\s*$/.test(value) && /\d+/.test(value)) {
        return parseNumber(value);
    }
    const searchUnits = [long, short, ...(alternates || [])];
    const baseRegex = /^(\+?-?\s*\d*(\.\d*)?(e[-+]?\d+)?)\s*(\S*)$/i;
    let [, numerical, , , units] = baseRegex.exec(value) || [];
    units = units?.trim();
    const unitsLower = units?.toLowerCase();
    numerical = numerical?.replace(/\s+/g, '');
    if (!units || units.length === 0) {
        return parseNumber(numerical);
    }
    let siChars = units;
    for (let searchUnit of searchUnits) {
        if (!searchUnit) {
            continue;
        }
        searchUnit = searchUnit.toLowerCase();
        if (unitsLower && unitsLower.endsWith(searchUnit)) {
            siChars = units.substring(0, unitsLower.lastIndexOf(searchUnit)).trim();
            break;
        }
    }
    if (usesSIPrefixes && siChars.length > 0) {
        for (const si of exports.siPrefixes) {
            if (si.match(siChars)) {
                return correctFloatingPointErrors(si.transform(numerical));
            }
        }
    }
    if (typeof siChars === 'undefined' || siChars.length > 0) {
        if (usesSIPrefixes) {
            throw new Error(`Error parsing SI value: '${value}'. Could not match one of expected units: '${searchUnits.join("', '")}'`);
        }
        else {
            throw new Error(`Error parsing value: '${value}'. Could not match one of expected units: '${searchUnits.join("', '")}'`);
        }
    }
    return parseNumber(numerical);
}
function parseNumber(num) {
    if (typeof num === 'number') {
        return num;
    }
    else if (typeof num === 'string') {
        num = num.replace(/\s+/g, '');
        return Number(num);
    }
    else {
        throw new Error(`Cannot parse number from ${typeof num}: ${num}`);
    }
}
//# sourceMappingURL=SIValueTool.js.map